import { z } from "zod";

import type { ToolCallTimelineItem } from "../../agent-sdk-types.js";
import { deriveOpencodeToolDetail } from "./tool-call-detail-parser.js";

type OpencodeToolCallParams = {
  toolName: string;
  callId?: string | null;
  status?: unknown;
  input?: unknown;
  output?: unknown;
  error?: unknown;
  metadata?: Record<string, unknown>;
  attachments?: unknown;
  /**
   * Known MCP server ids for this agent (config + allow-list). Used to rewrite
   * OpenCode's `<server>_<tool>` names into the canonical `mcp__<server>__<tool>`
   * form so downstream consumers recognise them uniformly.
   */
  mcpServerIds?: readonly string[];
};

/**
 * Rewrite an OpenCode tool name into the canonical `mcp__<server>__<tool>` form
 * when it belongs to a known MCP server. OpenCode emits MCP tools as
 * `<server>_<tool>` (single underscore, no prefix), while Claude's native
 * `mcp__<server>__<tool>` is the canon every consumer speaks.
 *
 * Built-in tools (`bash`, `read`, …) match no server id and pass through
 * unchanged. When several server ids prefix-match (e.g. `appostle` and
 * `appostle_voice`), the LONGEST wins so the tool segment is correct.
 */
function canonicalizeOpencodeMcpToolName(name: string, mcpServerIds: readonly string[]): string {
  if (name.startsWith("mcp__")) {
    return name;
  }
  let best: string | undefined;
  for (const id of mcpServerIds) {
    if (id.length === 0) {
      continue;
    }
    if (name.startsWith(`${id}_`) && (best === undefined || id.length > best.length)) {
      best = id;
    }
  }
  if (best === undefined) {
    return name;
  }
  const tool = name.slice(best.length + 1);
  if (tool.length === 0) {
    return name;
  }
  return `mcp__${best}__${tool}`;
}

const FAILED_STATUSES = new Set(["error", "failed", "failure"]);
const CANCELED_STATUSES = new Set(["canceled", "cancelled", "aborted", "interrupted"]);
const COMPLETED_STATUSES = new Set(["complete", "completed", "success", "succeeded", "done"]);

const OpencodeToolCallStatusSchema = z.enum(["running", "completed", "failed", "canceled"]);

const OpencodeRawToolCallSchema = z
  .object({
    toolName: z.string().min(1),
    callId: z.string().optional().nullable(),
    status: z.unknown().optional(),
    input: z.unknown().optional(),
    output: z.unknown().optional(),
    error: z.unknown().optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    attachments: z.unknown().optional(),
  })
  .passthrough();

const OpencodeNormalizedToolCallPass1Schema = OpencodeRawToolCallSchema.transform((raw) => {
  const input = raw.input ?? null;
  const output = raw.output ?? null;
  const error = raw.error ?? null;
  const attachments = raw.attachments ?? null;
  const callId =
    typeof raw.callId === "string" && raw.callId.trim().length > 0 ? raw.callId.trim() : null;
  let status: z.infer<typeof OpencodeToolCallStatusSchema>;

  if (error !== null) {
    status = "failed";
  } else if (typeof raw.status === "string") {
    const normalized = raw.status.trim().toLowerCase();
    if (FAILED_STATUSES.has(normalized)) {
      status = "failed";
    } else if (CANCELED_STATUSES.has(normalized)) {
      status = "canceled";
    } else if (COMPLETED_STATUSES.has(normalized)) {
      status = "completed";
    } else {
      status = "running";
    }
  } else {
    status = output !== null ? "completed" : "running";
  }

  return {
    callId,
    name: raw.toolName.trim(),
    input,
    output,
    error,
    metadata: raw.metadata,
    attachments,
    status,
  };
});

const OpencodeKnownToolNameSchema = z.union([
  // Shell
  z.literal("Bash"),
  z.literal("bash"),
  z.literal("shell"),
  z.literal("exec"),
  z.literal("exec_command"),
  z.literal("command"),
  // Read
  z.literal("Read"),
  z.literal("read"),
  z.literal("read_file"),
  z.literal("view_file"),
  // Write
  z.literal("Write"),
  z.literal("write"),
  z.literal("write_file"),
  z.literal("create_file"),
  // Edit
  z.literal("Edit"),
  z.literal("MultiEdit"),
  z.literal("multi_edit"),
  z.literal("edit"),
  z.literal("apply_patch"),
  z.literal("apply_diff"),
  z.literal("str_replace_editor"),
  // Grep / Glob
  z.literal("Grep"),
  z.literal("grep"),
  z.literal("Glob"),
  z.literal("glob"),
  // Web search
  z.literal("WebSearch"),
  z.literal("web_search"),
  z.literal("websearch"),
  // Generic search
  z.literal("search"),
  // Web fetch
  z.literal("WebFetch"),
  z.literal("web_fetch"),
  z.literal("WebFetchTool"),
  z.literal("web_fetch_tool"),
  z.literal("webfetch"),
  // Skill
  z.literal("Skill"),
  // speak
  z.literal("speak"),
]);

const OpencodeToolCallPass2BaseSchema = z.object({
  callId: z.string().min(1),
  name: z.string().min(1),
  input: z.unknown().nullable(),
  output: z.unknown().nullable(),
  error: z.unknown().nullable(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  attachments: z.unknown().nullable().optional(),
  status: OpencodeToolCallStatusSchema,
  toolKind: z.enum(["known", "other"]),
});

const OpencodeToolCallPass2InputSchema = OpencodeToolCallPass2BaseSchema.omit({
  toolKind: true,
});

const OpencodeToolCallPass2EnvelopeSchema = z.union([
  OpencodeToolCallPass2InputSchema.extend({
    name: OpencodeKnownToolNameSchema,
  }).transform((normalized) => ({
    ...normalized,
    name: normalized.name.trim(),
    toolKind: "known" as const,
  })),
  OpencodeToolCallPass2InputSchema.transform((normalized) => ({
    ...normalized,
    name: normalized.name.trim(),
    toolKind: "other" as const,
  })),
]);

const OpencodeToolCallPass2Schema = z.discriminatedUnion("toolKind", [
  OpencodeToolCallPass2BaseSchema.extend({
    toolKind: z.literal("known"),
    name: OpencodeKnownToolNameSchema,
  }),
  OpencodeToolCallPass2BaseSchema.extend({
    toolKind: z.literal("other"),
  }),
]);

type OpencodeToolCallPass2 = z.infer<typeof OpencodeToolCallPass2Schema>;

function toToolCallTimelineItem(normalized: OpencodeToolCallPass2): ToolCallTimelineItem {
  const detail = deriveOpencodeToolDetail(
    normalized.name,
    normalized.input,
    normalized.output,
    normalized.attachments,
  );
  if (normalized.status === "failed") {
    return {
      type: "tool_call",
      callId: normalized.callId,
      name: normalized.name,
      status: "failed",
      detail,
      error: normalized.error ?? { message: "Tool call failed" },
      ...(normalized.metadata ? { metadata: normalized.metadata } : {}),
    };
  }
  return {
    type: "tool_call",
    callId: normalized.callId,
    name: normalized.name,
    status: normalized.status,
    detail,
    error: null,
    ...(normalized.metadata ? { metadata: normalized.metadata } : {}),
  };
}

export function mapOpencodeToolCall(params: OpencodeToolCallParams): ToolCallTimelineItem | null {
  const pass1 = OpencodeNormalizedToolCallPass1Schema.safeParse(params);
  if (!pass1.success) {
    return null;
  }

  const canonicalName = canonicalizeOpencodeMcpToolName(pass1.data.name, params.mcpServerIds ?? []);
  const normalized =
    canonicalName === pass1.data.name ? pass1.data : { ...pass1.data, name: canonicalName };

  const pass2Envelope = OpencodeToolCallPass2EnvelopeSchema.safeParse(normalized);
  if (!pass2Envelope.success) {
    return null;
  }

  const pass2 = OpencodeToolCallPass2Schema.safeParse(pass2Envelope.data);
  if (!pass2.success) {
    return null;
  }

  return toToolCallTimelineItem(pass2.data);
}
